/**
 * Line Selection Hit Tester
 *
 * Provides hit testing utilities for line selection and interaction.
 * This module contains geometry and raycasting logic for:
 * - Finding prompts at screen positions
 * - Calculating distances from points to line segments
 * - Finding perpendicular snap targets
 * - Hit testing prompt spheres and direction lines
 *
 * These utilities can be reused across different tools that need
 * line selection capabilities.
 */

import * as THREE from 'three';
import { DetectedLine, LinePrompt } from './types';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';

export interface PromptLineHit {
  prompt: LinePrompt;
  side: 'positive' | 'negative';
  worldPoint: THREE.Vector3;
  /** True when the hit was snapped to an endpoint/intersection of the prompt line */
  snappedToEndpoint?: boolean;
}

export interface PerpendicularSnapTarget {
  prompt: LinePrompt;
  snapPoint: THREE.Vector3;
  previewEndPoint: THREE.Vector3;
  distance: number;
}

/**
 * Hit testing utilities for line selection
 */
export class LineSelectionHitTester {
  constructor(private coordinateTransformer: CoordinateTransformer) {}

  /**
   * Calculate the distance from a point to a line segment in 2D screen space.
   * Returns both the distance and the interpolation parameter t (0-1).
   */
  pointToLineSegmentDistance(point: THREE.Vector2, lineStart: THREE.Vector2, lineEnd: THREE.Vector2): { distance: number; t: number } {
    const lineDir = new THREE.Vector2().subVectors(lineEnd, lineStart);
    const lineLength = lineDir.length();

    if (lineLength < 0.001) {
      return { distance: point.distanceTo(lineStart), t: 0 };
    }

    lineDir.normalize();
    const toPoint = new THREE.Vector2().subVectors(point, lineStart);
    let t = toPoint.dot(lineDir) / lineLength;
    t = Math.max(0, Math.min(1, t));

    const closestPoint = lineStart.clone().add(lineDir.multiplyScalar(t * lineLength));
    return { distance: point.distanceTo(closestPoint), t };
  }

  /**
   * Find the closest prompt to a screen position within a threshold.
   * Useful for deletion/right-click operations with larger tolerance.
   */
  findClosestPrompt(screenPoint: THREE.Vector2, prompts: LinePrompt[], threshold = 30): LinePrompt | null {
    let closestPrompt: LinePrompt | null = null;
    let closestDistance = threshold;

    for (const prompt of prompts) {
      const screenPos = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      const distance = screenPoint.distanceTo(screenPos);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPrompt = prompt;
      }
    }

    return closestPrompt;
  }

  /**
   * Find the prompt whose center sphere is being hovered.
   * Uses a smaller threshold for precise sphere hit testing.
   */
  findHoveredPromptSphere(screenPoint: THREE.Vector2, prompts: LinePrompt[], threshold = 15): LinePrompt | null {
    let closestPrompt: LinePrompt | null = null;
    let closestDistance = threshold;

    for (const prompt of prompts) {
      const screenPos = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      const distance = screenPoint.distanceTo(screenPos);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPrompt = prompt;
      }
    }

    return closestPrompt;
  }

  /**
   * Find if a screen point is on a prompt's direction line (not the center sphere).
   * Returns the prompt, which side of the line, and the closest point on the line.
   * This is used for initiating drag operations from prompt direction lines.
   */
  findPromptLineAtPosition(screenPoint: THREE.Vector2, prompts: LinePrompt[], threshold = 10): PromptLineHit | null {
    let bestMatch: {
      prompt: LinePrompt;
      side: 'positive' | 'negative';
      worldPoint: THREE.Vector3;
      distance: number;
      snappedToEndpoint: boolean;
    } | null = null;

    // Screen-space distance threshold for snapping to an endpoint/intersection (pixels)
    const endpointSnapThreshold = 40;

    for (const prompt of prompts) {
      const centerScreen = this.coordinateTransformer.worldToScreen(prompt.worldPoint);

      // Skip if clicking near the center sphere (handled by findHoveredPromptSphere)
      if (screenPoint.distanceTo(centerScreen) < 15) {
        continue;
      }

      // Get the endpoints of the prompt line in world space
      const directionLineLength = 3.0;
      const defaultHalfLength = directionLineLength / 2;
      const posLength = prompt.positiveLength ?? defaultHalfLength;
      const negLength = prompt.negativeLength ?? defaultHalfLength;

      // Use connection points if available, otherwise calculate from length
      const posEnd = prompt.connections?.positive?.point?.clone() ?? prompt.worldPoint.clone().add(prompt.direction.clone().multiplyScalar(posLength));
      const negEnd = prompt.connections?.negative?.point?.clone() ?? prompt.worldPoint.clone().add(prompt.direction.clone().multiplyScalar(-negLength));

      // Convert to screen space
      const posEndScreen = this.coordinateTransformer.worldToScreen(posEnd);
      const negEndScreen = this.coordinateTransformer.worldToScreen(negEnd);

      // Check positive side (from center to posEnd)
      const posResult = this.pointToLineSegmentDistance(screenPoint, centerScreen, posEndScreen);
      if (posResult.distance < threshold && posResult.distance < (bestMatch?.distance ?? Infinity)) {
        // Check if close to the endpoint — snap to the actual intersection/endpoint
        const distToEndpoint = screenPoint.distanceTo(posEndScreen);
        const snapped = distToEndpoint < endpointSnapThreshold && prompt.connections?.positive;
        const worldPoint = snapped ? posEnd.clone() : new THREE.Vector3().lerpVectors(prompt.worldPoint, posEnd, posResult.t);
        bestMatch = {
          prompt,
          side: 'positive',
          worldPoint,
          distance: posResult.distance,
          snappedToEndpoint: !!snapped,
        };
      }

      // Check negative side (from center to negEnd)
      const negResult = this.pointToLineSegmentDistance(screenPoint, centerScreen, negEndScreen);
      if (negResult.distance < threshold && negResult.distance < (bestMatch?.distance ?? Infinity)) {
        // Check if close to the endpoint — snap to the actual intersection/endpoint
        const distToEndpoint = screenPoint.distanceTo(negEndScreen);
        const snapped = distToEndpoint < endpointSnapThreshold && prompt.connections?.negative;
        const worldPoint = snapped ? negEnd.clone() : new THREE.Vector3().lerpVectors(prompt.worldPoint, negEnd, negResult.t);
        bestMatch = {
          prompt,
          side: 'negative',
          worldPoint,
          distance: negResult.distance,
          snappedToEndpoint: !!snapped,
        };
      }
    }

    return bestMatch
      ? {
          prompt: bestMatch.prompt,
          side: bestMatch.side,
          worldPoint: bestMatch.worldPoint,
          snappedToEndpoint: bestMatch.snappedToEndpoint,
        }
      : null;
  }

  /**
   * Find the closest point on a line segment from a screen position.
   * Projects the screen click onto the line in screen space, then interpolates
   * in world space for accurate positioning regardless of zoom level.
   */
  closestPointOnLineFromScreen(line: DetectedLine, screenPoint: THREE.Vector2): THREE.Vector3 {
    // Get current screen positions of line endpoints
    const screenStart = this.coordinateTransformer.worldToScreen(line.start);
    const screenEnd = this.coordinateTransformer.worldToScreen(line.end);

    // Calculate projection in screen space
    const lineDir = new THREE.Vector2().subVectors(screenEnd, screenStart);
    const lineLength = lineDir.length();

    if (lineLength < 0.0001) {
      return line.start.clone();
    }

    lineDir.normalize();

    const toPoint = new THREE.Vector2().subVectors(screenPoint, screenStart);
    const t = toPoint.dot(lineDir);

    // Clamp to segment bounds and normalize to [0, 1]
    const normalizedT = Math.max(0, Math.min(1, t / lineLength));

    // Interpolate in world space using the normalized parameter
    return new THREE.Vector3().lerpVectors(line.start, line.end, normalizedT);
  }

  /**
   * Find the prompt line to snap to based on distance to capped line segment.
   * Selection uses distance to the capped segment (limited by positiveLength/negativeLength).
   * The snap point is capped to the segment for selection, but the final snap point
   * is on the infinite line (perpendicular projection).
   *
   * This is used for perpendicular line creation mode (Shift+click).
   * If lockedPrompt is provided, it is used directly instead of finding the closest.
   */
  findPerpendicularSnapTarget(worldPoint: THREE.Vector3, prompts: LinePrompt[], lockedPrompt?: LinePrompt): PerpendicularSnapTarget | null {
    if (prompts.length === 0) return null;

    let closestPrompt: LinePrompt | null = lockedPrompt ?? null;
    let closestDistance = Infinity;

    // If a locked prompt is provided, skip the search loop
    if (lockedPrompt) {
      closestDistance = 0; // Not used when locked
    }

    // Find the prompt with the closest capped line segment
    for (const prompt of lockedPrompt ? [] : prompts) {
      const linePoint = prompt.worldPoint;
      const lineDir = prompt.direction.clone().normalize();

      // Vector from line point to cursor world point
      const toPoint = new THREE.Vector3().subVectors(worldPoint, linePoint);

      // Project onto line direction to get the parameter t
      const t = toPoint.dot(lineDir);

      // Get the segment bounds from prompt lengths (default to some value if not set)
      const posLength = prompt.positiveLength ?? 1.0;
      const negLength = prompt.negativeLength ?? 1.0;

      // Clamp t to the segment bounds for distance calculation
      // t > 0 means positive direction, t < 0 means negative direction
      const clampedT = Math.max(-negLength, Math.min(posLength, t));

      // Point on the capped segment (used for selection comparison)
      const cappedPoint = linePoint.clone().add(lineDir.clone().multiplyScalar(clampedT));

      // Distance from cursor to the capped point
      const distance = worldPoint.distanceTo(cappedPoint);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestPrompt = prompt;
      }
    }

    if (!closestPrompt) return null;

    // Now calculate the perpendicular snap point on the infinite line of the selected prompt
    const linePoint = closestPrompt.worldPoint;
    const lineDir = closestPrompt.direction.clone().normalize();

    // Vector from line point to cursor world point
    const toPoint = new THREE.Vector3().subVectors(worldPoint, linePoint);

    // Project onto line direction to get the parameter t (unclamped for infinite line)
    const t = toPoint.dot(lineDir);

    // Closest point on the infinite line (perpendicular projection)
    const snapPoint = linePoint.clone().add(lineDir.clone().multiplyScalar(t));

    // Distance from cursor to snap point (perpendicular distance on infinite line)
    const distance = worldPoint.distanceTo(snapPoint);

    return {
      prompt: closestPrompt,
      snapPoint,
      previewEndPoint: worldPoint.clone(),
      distance,
    };
  }

  /**
   * Project a 3D world point to 2D screen coordinates.
   * Convenience method that wraps CoordinateTransformer.
   */
  projectToScreen(worldPoint: THREE.Vector3): THREE.Vector2 {
    return this.coordinateTransformer.worldToScreen(worldPoint);
  }
}
